import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Separator } from "../components/ui/separator";
import {
    User,
    Mail,
    Phone,
    Shield,
    Building2,
    Calendar,
    Edit,
    Settings,
    Eye,
    EyeOff,
    Key,
    Palette,
    Users,
    FileText,
    Clock,
    CheckCircle,
    XCircle,
    Grid3X3,
    UserCheck
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { getFullName } from "../services/common/util";
import { format } from "date-fns";
import { UpdatePasswordModal } from "../components/staff/UpdatePasswordModal";

export function ViewProfile() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [showSensitiveInfo, setShowSensitiveInfo] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);

    const handlePasswordUpdateSuccess = () => {
        setShowPasswordModal(false);
    };

    if (!user) {
        return (
            <div className="container mx-auto px-4 sm:px-6">
                <div className="text-center py-12">
                    <h3 className="text-lg font-medium text-gray-900 mb-1">User not found</h3>
                    <p className="text-gray-500">Please log in to view your profile.</p>
                </div>
            </div>
        );
    }

    const getInitials = (name) => {
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);
    };

    const getPositionColor = (position) => {
        switch (position.role) {
            case "admin":
                return "bg-purple-100 text-purple-800";
            case "floorManager":
                return "bg-yellow-100 text-yellow-800";
            case "deskManager":
                return "bg-blue-100 text-blue-800";
            default:
                return "bg-green-100 text-green-800";
        }
    };

    const handleEditProfile = () => {
        navigate("/profile/edit");
    };

    return (
        <div className="container mx-auto px-4 sm:px-6">
            <div className="mb-6">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
                    <div className="flex space-x-3">
                        <Button
                            onClick={() => setShowPasswordModal(true)}
                            variant="outline"
                            className="border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100"
                        >
                            <Key className="h-4 w-4 mr-2" />
                            Update Password
                        </Button>
                        <Button onClick={handleEditProfile}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Profile
                        </Button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Overview */}
                <div className="lg:col-span-1">
                    <Card>
                        <CardHeader className="text-center pb-4">
                            <Avatar className="w-24 h-24 mx-auto mb-4">
                                <AvatarFallback className="text-2xl font-bold bg-blue-100 text-blue-600">
                                    {getInitials(getFullName(user))}
                                </AvatarFallback>
                            </Avatar>
                            <CardTitle className="text-xl">{getFullName(user)}</CardTitle>
                            <div className="flex flex-col items-center space-y-2 mt-3">
                                <Badge variant="outline" className={getPositionColor(user.position)}>
                                    {user.position.name}
                                </Badge>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center space-x-3">
                                <Mail className="h-4 w-4 text-gray-400" />
                                <span className="text-sm">{user.email}</span>
                            </div>
                            {user.userName && (
                                <div className="flex items-center space-x-3">
                                    <User className="h-4 w-4 text-gray-400" />
                                    <span className="text-sm">@{user.userName}</span>
                                </div>
                            )}
                            {user.agentDesk && (
                                <div className="flex items-center space-x-3">
                                    <Building2 className="h-4 w-4 text-gray-400" />
                                    <span className="text-sm">{user.agentDesk.name}</span>
                                </div>
                            )}
                            <div className="flex items-center space-x-3">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span className="text-sm">
                                    Joined {user.createdAt ? format(new Date(user.createdAt), "MMM dd, yyyy") : "N/A"}
                                </span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Quick Stats */}
                    <Card className="mt-6">
                        <CardHeader>
                            <CardTitle className="text-lg">Quick Stats</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">My Leads</span>
                                <Badge variant="outline">{user.leadCount || 0}</Badge>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Fast Login</span>
                                <div className="flex items-center">
                                    {user.canFastLogin ? (
                                        <CheckCircle className="h-4 w-4 text-green-600" />
                                    ) : (
                                        <XCircle className="h-4 w-4 text-red-600" />
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">View Leads Scope</span>
                                <Badge variant="secondary" className="text-xs">
                                    {user.viewLeadsScope || "Assigned"}
                                </Badge>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Detailed Information */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Personal Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <User className="h-5 w-5 mr-2" />
                                Personal Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-medium text-gray-600">First Name</label>
                                    <p className="text-sm text-gray-900 mt-1">{user.firstName || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Last Name</label>
                                    <p className="text-sm text-gray-900 mt-1">{user.lastName || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">User Name</label>
                                    <p className="text-sm text-gray-900 mt-1">{user.userName || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Email Address</label>
                                    <p className="text-sm text-gray-900 mt-1">{user.email}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Position</label>
                                    <Badge variant="outline" className={`${getPositionColor(user.position)} ml-2`}>
                                        {user.position.name}
                                    </Badge>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* System Integration */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <Settings className="h-5 w-5 mr-2" />
                                System Integration
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Commpeak ID</label>
                                    <p className="text-sm text-gray-900 mt-1">{user.commpeak || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Voiso ID</label>
                                    <p className="text-sm text-gray-900 mt-1">{user.voiso || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">SquareTalk ID</label>
                                    <p className="text-sm text-gray-900 mt-1">{user.squaretalk || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">GlobalCall SIP Account</label>
                                    <p className="text-sm text-gray-900 mt-1">{user.sipAccount || "-"}</p>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Comment Color</label>
                                    <div className="flex items-center space-x-2 mt-1">
                                        <div
                                            className="w-6 h-6 rounded border"
                                            style={{ backgroundColor: user.commentColor || "#000000" }}
                                        ></div>
                                        <span className="text-sm text-gray-900">{user.commentColor || "#000000"}</span>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-gray-600">Fast Login Enabled</label>
                                    <div className="flex items-center space-x-2 mt-1">
                                        {user.canFastLogin ? (
                                            <>
                                                <CheckCircle className="h-4 w-4 text-green-600" />
                                                <span className="text-sm text-green-600">Enabled</span>
                                            </>
                                        ) : (
                                            <>
                                                <XCircle className="h-4 w-4 text-red-600" />
                                                <span className="text-sm text-red-600">Disabled</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Permissions Overview */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <Shield className="h-5 w-5 mr-2" />
                                Permissions Overview
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-6">
                                {/* Lead Permissions */}
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                        <Users className="h-4 w-4 mr-2" />
                                        Lead Permissions
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {user.permissions?.leads &&
                                            Object.entries(user.permissions.leads).map(([permission, enabled]) => (
                                                <div key={permission} className="flex items-center space-x-2">
                                                    {enabled ? (
                                                        <CheckCircle className="h-3 w-3 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-3 w-3 text-red-600" />
                                                    )}
                                                    <span className="text-xs capitalize">
                                                        {permission.replace(/([A-Z])/g, " $1").trim()}
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                </div>

                                <Separator />

                                {/* Staff Permissions */}
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                        <UserCheck className="h-4 w-4 mr-2" />
                                        Staff Permissions
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                        {user.permissions?.staff &&
                                            Object.entries(user.permissions.staff).map(([permission, enabled]) => (
                                                <div key={permission} className="flex items-center space-x-2">
                                                    {enabled ? (
                                                        <CheckCircle className="h-3 w-3 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-3 w-3 text-red-600" />
                                                    )}
                                                    <span className="text-xs capitalize">{permission}</span>
                                                </div>
                                            ))}
                                    </div>
                                </div>

                                <Separator />

                                {/* Desk Permissions */}
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                        <Building2 className="h-4 w-4 mr-2" />
                                        Desk Permissions
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                        {user.permissions?.desks &&
                                            Object.entries(user.permissions.desks).map(([permission, enabled]) => (
                                                <div key={permission} className="flex items-center space-x-2">
                                                    {enabled ? (
                                                        <CheckCircle className="h-3 w-3 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-3 w-3 text-red-600" />
                                                    )}
                                                    <span className="text-xs capitalize">{permission}</span>
                                                </div>
                                            ))}
                                    </div>
                                </div>

                                <Separator />

                                {/* Grid Leads Columns Permissions */}
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                        <Grid3X3 className="h-4 w-4 mr-2" />
                                        Grid Leads Column Visibility
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {user.permissions?.gridLeadsColumns &&
                                            Object.entries(user.permissions.gridLeadsColumns).map(
                                                ([column, enabled]) => (
                                                    <div key={column} className="flex items-center space-x-2">
                                                        {enabled ? (
                                                            <CheckCircle className="h-3 w-3 text-green-600" />
                                                        ) : (
                                                            <XCircle className="h-3 w-3 text-red-600" />
                                                        )}
                                                        <span className="text-xs capitalize">
                                                            {column.replace(/([A-Z])/g, " $1").trim()}
                                                        </span>
                                                    </div>
                                                )
                                            )}
                                    </div>
                                </div>

                                <Separator />

                                {/* Lead Field Permissions */}
                                <div>
                                    <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                                        <FileText className="h-4 w-4 mr-2" />
                                        Lead Field Edit Permissions
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                        {user.permissions?.leadFields &&
                                            Object.entries(user.permissions.leadFields).map(([field, enabled]) => (
                                                <div key={field} className="flex items-center space-x-2">
                                                    {enabled ? (
                                                        <CheckCircle className="h-3 w-3 text-green-600" />
                                                    ) : (
                                                        <XCircle className="h-3 w-3 text-red-600" />
                                                    )}
                                                    <span className="text-xs capitalize">
                                                        {field.replace(/([A-Z])/g, " $1").trim()}
                                                    </span>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Leads Visibility */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center">
                                <Eye className="h-5 w-5 mr-2" />
                                Leads Visibility Settings
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-gray-600">View Leads Scope</label>
                                    <Badge variant="outline" className="ml-2">
                                        {user.viewLeadsScope || "Assigned"}
                                    </Badge>
                                </div>
                                {user.viewLeadsAllowedDesks && user.viewLeadsAllowedDesks.length > 0 && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-600">Allowed Desks</label>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {user.viewLeadsAllowedDesks.map((desk, index) => (
                                                <Badge key={index} variant="secondary" className="text-xs">
                                                    {desk.name || desk}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Update Password Modal */}
            <UpdatePasswordModal
                open={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                onSuccess={handlePasswordUpdateSuccess}
                staff={user}
            />
        </div>
    );
}
